import { useState, useEffect } from 'react'
import { getAlerts, AlertsData } from '../services/alerts'
import LoadingSpinner from '../components/LoadingSpinner'

/**
 * H3 del roadmap (Fase C #8): "esto complementa la auditoría manual, no
 * la sustituye" — Alfonso y Elizabeth fueron explícitos en que el proceso
 * manual seguirá existiendo porque detecta incidencias que un sistema
 * automático no vería.
 *
 * Con solo 17 días de historia real (y muchas campañas con bastantes
 * menos), cada alerta de volumen expone su propio "sampleSize" — cuántos
 * días respaldan la media con la que se compara — para que el equipo
 * juzgue la confianza en vez de tratar cualquier media como si fuera un
 * patrón sólido.
 */
const Alerts = () => {
  const [data, setData] = useState<AlertsData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchData = async () => {
    try {
      setLoading(true)
      const res = await getAlerts()
      setData(res.data)
      setError(null)
    } catch (err) {
      setError('Error al cargar las alertas')
      console.error('Error fetching alerts:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [])

  if (loading) {
    return <LoadingSpinner />
  }

  if (error || !data) {
    return (
      <div className="card p-6 text-center text-dashboard-muted">{error || 'Sin datos'}</div>
    )
  }

  const totalAlerts = data.volumeAlerts.length + data.uniqueRatioAlerts.length + data.cappingAlerts.length

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold text-dashboard-text flex items-center">
          <span className="mr-3">🚨</span>
          ALERTAS
        </h1>
        <p className="text-dashboard-muted mt-1">
          Complementa la auditoría manual, no la sustituye — detecta desviaciones para no
          depender de revisar cada campaña a mano cada día, pero el proceso manual sigue
          siendo necesario para incidencias que un sistema automático no vería.
        </p>
      </div>

      {totalAlerts === 0 ? (
        <div className="card p-6 text-center text-dashboard-muted">
          Sin alertas activas ahora mismo. 🎉
        </div>
      ) : (
        <>
          {/* Alertas de volumen */}
          <div className="card">
            <div className="p-6 border-b border-dashboard-border">
              <h2 className="text-xl font-bold text-dashboard-text">
                Desviaciones de volumen ({data.volumeAlerts.length})
              </h2>
              <p className="text-sm text-dashboard-muted mt-1">
                Compara el día más reciente con la media histórica del mismo tipo de día
                (laborable/fin de semana) — solo se alerta con al menos{' '}
                {data.thresholds.minSamplesForBaseline} días de historia comparable y una
                desviación de al menos {data.thresholds.volumeDeviationThresholdPct}%.
              </p>
            </div>
            {data.volumeAlerts.length === 0 ? (
              <div className="p-6 text-center text-dashboard-muted text-sm">Sin desviaciones de volumen</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="table-modern">
                  <thead>
                    <tr>
                      <th>Tipo</th>
                      <th>Campaña / Source Page</th>
                      <th>Fecha</th>
                      <th>Hoy</th>
                      <th>Media histórica</th>
                      <th>Desviación</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.volumeAlerts.map((a, i) => (
                      <tr key={i}>
                        <td>
                          <span className="badge badge-info">{a.type === 'campaign' ? 'Campaña' : 'Source Page'}</span>
                        </td>
                        <td className="max-w-xs truncate" title={a.name}>
                          {a.name}
                        </td>
                        <td className="text-sm text-dashboard-muted">
                          {a.date.slice(0, 10)} ({a.dayType === 'weekday' ? 'laborable' : 'fin de semana'})
                        </td>
                        <td className="font-semibold">{a.today.toLocaleString()}</td>
                        <td className="text-sm text-dashboard-muted">
                          {a.historicalMean.toLocaleString()} (n={a.sampleSize})
                        </td>
                        <td>
                          <span className={`badge ${a.direction === 'down' ? 'badge-error' : 'badge-warning'}`}>
                            {a.direction === 'down' ? '▼' : '▲'} {Math.abs(a.percentDeviation).toFixed(0)}%
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Alertas de Único/Total */}
          <div className="card">
            <div className="p-6 border-b border-dashboard-border">
              <h2 className="text-xl font-bold text-dashboard-text">
                Cambios en el reparto Único/Total ({data.uniqueRatioAlerts.length})
              </h2>
              <p className="text-sm text-dashboard-muted mt-1">
                Compara los 2 días más recientes con datos de cada source page — alerta si el
                % de leads vía cliente directo (frente a vía LB) cambia más de{' '}
                {data.thresholds.uniqueRatioDeltaThresholdPp} puntos porcentuales de un día
                al otro.
              </p>
            </div>
            {data.uniqueRatioAlerts.length === 0 ? (
              <div className="p-6 text-center text-dashboard-muted text-sm">Sin cambios drásticos en el reparto</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="table-modern">
                  <thead>
                    <tr>
                      <th>Source Page</th>
                      <th>Día anterior</th>
                      <th>Día reciente</th>
                      <th>Cambio</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.uniqueRatioAlerts.map((a, i) => (
                      <tr key={i}>
                        <td className="max-w-xs truncate" title={a.sourcePage}>
                          {a.sourcePage}
                        </td>
                        <td className="text-sm text-dashboard-muted">
                          {a.previousDate.slice(0, 10)}: {a.previousUniquePct.toFixed(1)}% directo
                        </td>
                        <td className="text-sm text-dashboard-muted">
                          {a.latestDate.slice(0, 10)}: {a.latestUniquePct.toFixed(1)}% directo
                        </td>
                        <td>
                          <span className={`badge ${a.deltaPercentagePoints < 0 ? 'badge-error' : 'badge-warning'}`}>
                            {a.deltaPercentagePoints > 0 ? '+' : ''}
                            {a.deltaPercentagePoints.toFixed(1)} pp
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Alertas de capping */}
          <div className="card">
            <div className="p-6 border-b border-dashboard-border">
              <h2 className="text-xl font-bold text-dashboard-text">
                Proyección de capping ({data.cappingAlerts.length})
              </h2>
              <p className="text-sm text-dashboard-muted mt-1">
                Solo campañas con capping configurado manualmente (Campaign Details). Proyecta
                en cuántos días se alcanzaría el límite al ritmo de los últimos días.
              </p>
            </div>
            {data.cappingAlerts.length === 0 ? (
              <div className="p-6 text-center text-dashboard-muted text-sm">
                Ninguna campaña tiene capping configurado todavía
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="table-modern">
                  <thead>
                    <tr>
                      <th>Campaña</th>
                      <th>Capping</th>
                      <th>Leads hasta ahora</th>
                      <th>Ritmo diario</th>
                      <th>Proyección</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.cappingAlerts.map((a, i) => (
                      <tr key={i}>
                        <td className="max-w-xs truncate" title={a.campaign}>
                          {a.campaign}
                        </td>
                        <td className="font-semibold">{a.capping.toLocaleString()}</td>
                        <td>{a.leadsSoFar.toLocaleString()}</td>
                        <td className="text-sm text-dashboard-muted">{a.dailyPace.toFixed(1)}/día</td>
                        <td>
                          {a.alreadyReached ? (
                            <span className="badge badge-error">Ya alcanzado</span>
                          ) : a.projectedDaysToReach !== null ? (
                            <span className="badge badge-warning">~{a.projectedDaysToReach} días</span>
                          ) : (
                            <span className="text-xs text-dashboard-muted">Sin ritmo reciente para proyectar</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  )
}

export default Alerts
